import { formatDateTime } from './optionsHistoryUtils';
import { formatNative } from './optionsDashboardUtils';

export const PORTFOLIO_ACTIVITY_ROLE_OPTIONS = ['HEDGE', 'DIRECTIONAL', 'DIRECTIONAL_OVERLAY', 'PORTFOLIO_BALANCE', 'DIRECTIONAL_SPOT', 'UNCLASSIFIED'];

export const getPortfolioRoleLabel = (role, t) => {
  const normalized = String(role || '').toUpperCase();
  return {
    OPTIONS_STRATEGY: t('common.optionsStrategy'),
    HEDGE: t('common.hedge'),
    DIRECTIONAL: t('common.directional'),
    DIRECTIONAL_OVERLAY: t('common.directionalOverlay'),
    PORTFOLIO_BALANCE: t('common.portfolioBalance'),
    DIRECTIONAL_SPOT: t('common.directionalSpot'),
    UNCLASSIFIED: t('common.unclassified'),
  }[normalized] || normalized || '—';
};

export const getInstrumentTypeLabel = (instrumentType, t) => {
  const normalized = String(instrumentType || '').toUpperCase();
  return {
    OPTION: t('common.optionInstrument'),
    FUTURE: t('common.futureInstrument'),
    PERPETUAL: t('common.perpetualInstrument'),
    SPOT: t('common.spotInstrument'),
  }[normalized] || normalized || '—';
};

export const getPortfolioRoleBadgeMeta = (role) => {
  const normalized = String(role || '').toUpperCase();
  if (normalized === 'HEDGE') {
    return { className: 'border-[#38BDF8]/20 bg-[#38BDF8]/10 text-[#C7F0FF]' };
  }
  if (normalized === 'DIRECTIONAL') {
    return { className: 'border-[#4ADE80]/20 bg-[#4ADE80]/10 text-[#D8FBE1]' };
  }
  if (normalized === 'DIRECTIONAL_OVERLAY') {
    return { className: 'border-[#F59E0B]/20 bg-[#F59E0B]/10 text-[#FFE29A]' };
  }
  if (normalized === 'PORTFOLIO_BALANCE') {
    return { className: 'border-white/10 bg-white/[0.05] text-white/80' };
  }
  if (normalized === 'DIRECTIONAL_SPOT') {
    return { className: 'border-[#22C55E]/20 bg-[#22C55E]/10 text-[#BDFACD]' };
  }
  return { className: 'border-white/10 bg-white/[0.05] text-white/70' };
};

export const formatEquivalentDelta = (value, underlying) => {
  if (value === null || value === undefined) return '—';
  const digits = String(underlying || '').toUpperCase() === 'BTC' ? 4 : 3;
  const native = formatNative(value, underlying || 'USD', digits);
  return Number(value) > 0 ? `+${native}` : native;
};

export const formatMarketSideLabel = (marketSide) => {
  const normalized = String(marketSide || '').toUpperCase();
  if (normalized === 'LONG') return 'Long';
  if (normalized === 'SHORT') return 'Short';
  return null;
};

const nativeDigitsForCurrency = (currency) => {
  const normalized = String(currency || '').toUpperCase();
  if (normalized === 'BTC') return 4;
  if (normalized === 'ETH') return 3;
  return 2;
};

// Presentation-only: formats an already-known execution amount with its real currency
// code. Never converts or infers a value that isn't already on the activity record.
export const formatActivityAmount = (value, currency) => {
  if (value === null || value === undefined || !currency) return '—';
  return formatNative(Math.abs(Number(value)), currency, nativeDigitsForCurrency(currency));
};

export const formatActivityFee = (value, currency) => {
  if (value === null || value === undefined || !currency) return '—';
  const numeric = Math.abs(Number(value));
  if (numeric === 0) return formatActivityAmount(0, currency);
  return `-${formatActivityAmount(numeric, currency)}`;
};

// Re-reads the already-recorded execution fields (side, base amount, real execution
// price) to describe a spot trade economically as "sold X / received Y".
// IMPORTANT: cashflow_native is NOT the quote-side proceeds - Deribit's transaction log
// is fetched per-currency, so cashflow_native is booked in the SAME currency as
// amount_raw (the base currency's own ledger), which is why it previously showed the
// same number on both "sold" and "received". The quote amount is derived from the real
// base amount and the real execution price already returned by Deribit
// (quote = base_amount x average_price), per instrument-metadata convention for spot.
export const buildSpotRebalanceSummary = (item) => {
  const base = item?.base_currency || item?.underlying || null;
  const quote = item?.quote_currency || null;
  const sideRaw = String(item?.side || item?.market_side || '').toLowerCase();
  const isBaseSold = sideRaw.includes('sell') || sideRaw.includes('short');
  const baseAmount = item?.amount_raw !== null && item?.amount_raw !== undefined ? Math.abs(Number(item.amount_raw)) : null;
  const price = item?.average_price !== null && item?.average_price !== undefined ? Math.abs(Number(item.average_price)) : null;
  const quoteAmount = baseAmount !== null && price !== null ? baseAmount * price : null;
  const feeCurrency = item?.settlement_currency || base;

  return {
    fromCurrency: isBaseSold ? base : quote,
    toCurrency: isBaseSold ? quote : base,
    soldAmount: isBaseSold ? baseAmount : quoteAmount,
    soldCurrency: isBaseSold ? base : quote,
    receivedAmount: isBaseSold ? quoteAmount : baseAmount,
    receivedCurrency: isBaseSold ? quote : base,
    feeCurrency,
  };
};

export const getLinkScopeLabel = (item, t) => {
  if (item?.link_scope === 'POSITION_CYCLE' && item?.linked_campaign_id) {
    return `${t('history.positionCycleLink')} · ${item.linked_campaign_id}`;
  }
  if (item?.link_scope === 'OPERATION' && item?.linked_operation_id) {
    return `${t('history.operationLink')} · ${item.linked_operation_id}`;
  }
  if (item?.link_scope === 'PORTFOLIO_UNDERLYING' && item?.linked_underlying) {
    return `${t('history.portfolioUnderlyingLink')} · ${item.linked_underlying}`;
  }
  return t('history.unlinkedActivity');
};

export const getActivityTimestampLabel = (item, isPortuguese) => {
  return formatDateTime(item?.event_at, isPortuguese ? 'pt' : 'en');
};

export const getActivityConfidenceBadgeMeta = (confidence) => {
  const normalized = String(confidence || '').toUpperCase();
  if (normalized === 'HIGH') return { className: 'border-[#22C55E]/18 bg-[#22C55E]/10 text-[#D8FBE1]' };
  if (normalized === 'MEDIUM') return { className: 'border-[#F59E0B]/18 bg-[#F59E0B]/10 text-[#FFE29A]' };
  return { className: 'border-white/10 bg-white/[0.05] text-white/65' };
};

export const activityMatchesHistoryFilters = (activity, rangeBounds, effectiveUnderlying) => {
  const timestamp = activity?.event_at;
  if (rangeBounds?.start && timestamp && timestamp < rangeBounds.start) return false;
  if (rangeBounds?.end && timestamp && timestamp > rangeBounds.end) return false;
  if (String(effectiveUnderlying || 'all').toUpperCase() !== 'ALL') {
    return String(activity?.underlying || '').toUpperCase() === String(effectiveUnderlying).toUpperCase();
  }
  return true;
};